import { useState } from "react";

import dayjs from "dayjs";
import {
  Check,
  Loader2,
  MoreHorizontal,
  Pencil,
  Printer,
  X,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { EmptyState } from "~/components/ui/empty-state";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getProductionUnitLabel } from "~/constants/units";
import {
  useCancelProductTransfer,
  usePendingReceipts,
  useReceiveProductTransfer,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { useProfileStore } from "~/store/profileStore";
import { warehouseToPlantLabel } from "~/types/purchasing";

import { EditTransferDialog } from "./EditTransferDialog";

import type { ProductTransferWithNames } from "~/types/productTransfer";

const fmtQty = (n: number) => n.toLocaleString("es-CL");

const loteOf = (t: ProductTransferWithNames) =>
  t.lot_number ??
  t.production_batch?.lot_number ??
  t.production_batch?.batch_number ??
  "Sin lote";

const performerName = (p?: {
  name: string | null;
  lastname: string | null;
} | null) => {
  if (!p) return "—";
  return `${p.name ?? ""} ${p.lastname ?? ""}`.trim() || "—";
};

interface TransferReceptionTabProps {
  onPrint: (transferId: string) => void;
  printingId: string | null;
}

// Pending-receipt inbox: producto terminado transfers still in transit. The
// receiving bodega verifies the physical stock against the guía, then confirms
// ("CHECK") to deposit it in the destination.
export function TransferReceptionTab({
  onPrint,
  printingId,
}: TransferReceptionTabProps) {
  const { toast } = useToast();
  const { id: userId, role } = useProfileStore();
  const { data: pending = [], isLoading } = usePendingReceipts();
  const receive = useReceiveProductTransfer();
  const cancel = useCancelProductTransfer();
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ProductTransferWithNames | null>(
    null
  );

  // The guía may be edited by its creator, or by an admin/superadmin (the RPC
  // enforces the same rule server-side).
  const canEdit = (t: ProductTransferWithNames) =>
    userId === t.performed_by_profile?.id ||
    role === "superadmin" ||
    role === "admin";

  const handleReceive = (t: ProductTransferWithNames) => {
    if (!userId) return;
    setActioningId(t.id);
    receive.mutate(
      { transferId: t.id, performedBy: userId },
      {
        onSuccess: () => {
          toast({
            title: "Recepción confirmada",
            description: `Guía ${t.transfer_number} recibida en ${
              t.to_warehouse?.code
                ? warehouseToPlantLabel(t.to_warehouse.code)
                : "destino"
            }.`,
          });
        },
        onError: (error) =>
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          }),
        onSettled: () => setActioningId(null),
      }
    );
  };

  const handleCancel = (t: ProductTransferWithNames) => {
    if (!userId) return;
    setActioningId(t.id);
    cancel.mutate(
      { transferId: t.id, performedBy: userId },
      {
        onSuccess: () => {
          toast({
            title: "Traslado anulado",
            description: `Guía ${t.transfer_number} anulada. El stock volvió a ${
              t.from_warehouse?.code
                ? warehouseToPlantLabel(t.from_warehouse.code)
                : "origen"
            }.`,
          });
        },
        onError: (error) =>
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          }),
        onSettled: () => setActioningId(null),
      }
    );
  };

  if (isLoading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (pending.length === 0) {
    return (
      <EmptyState
        icon={Check}
        title="Sin traslados por recibir"
        description="No hay producto terminado en tránsito pendiente de recepción."
      />
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead>
                <TableHead>Fecha envío</TableHead>
                <TableHead>Traslado</TableHead>
                <TableHead>Detalle</TableHead>
                <TableHead>Enviado por</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((t) => {
                const busy = actioningId === t.id;
                return (
                  <TableRow key={t.id} className="hover:bg-muted/30">
                    <TableCell className="font-medium">
                      {t.transfer_number}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {dayjs(t.transferred_at).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {warehouseToPlantLabel(t.from_warehouse?.code)} →{" "}
                      {warehouseToPlantLabel(t.to_warehouse?.code)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {t.product?.short_name ?? "Producto"} · Lote {loteOf(t)} ·{" "}
                      {fmtQty(t.boxes)}{" "}
                      {getProductionUnitLabel(
                        t.product?.base_measurement_unit ??
                          t.product?.unit_code ??
                          null,
                        t.boxes
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {performerName(t.performed_by_profile)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          className="h-9"
                          disabled={busy}
                          onClick={() => handleReceive(t)}
                        >
                          {busy ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="mr-1 h-4 w-4" />
                          )}
                          Confirmar recepción
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 w-9 p-0"
                              disabled={busy}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onPrint(t.id)}
                              disabled={printingId === t.id}
                            >
                              <Printer className="mr-2 h-4 w-4" />
                              Imprimir guía
                            </DropdownMenuItem>
                            {canEdit(t) && (
                              <DropdownMenuItem
                                onClick={() => setEditTarget(t)}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar guía
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-red-700 focus:text-red-700"
                              onClick={() => handleCancel(t)}
                            >
                              <X className="mr-2 h-4 w-4" />
                              Anular traslado
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EditTransferDialog
        transfer={editTarget}
        open={editTarget !== null}
        onOpenChange={(o) => !o && setEditTarget(null)}
      />
    </>
  );
}
